import React from 'react';
import { cn } from '../../lib/utils';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Options for the select dropdown */
  options: SelectOption[];
  /** Placeholder text for empty selection */
  placeholder?: string;
  /** Show error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
}

/**
 * Select dropdown component with error state handling
 *
 * @example
 * ```tsx
 * <Select options={[{value: '1', label: 'Option 1'}]} />
 * <Select options={options} placeholder="Choose one" />
 * <Select options={options} error errorMessage="Selection required" />
 * ```
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, placeholder, error = false, errorMessage, className, ...props }, ref) => {
    return (
      <div className="w-full">
        <select
          ref={ref}
          className={cn(
            'input',
            'w-full',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="">{placeholder}</option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && errorMessage && (
          <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
